/**
 * Единый файл сообщений об ошибках валидации.
 * Общие поля форм — все ошибки здесь, без разбиения по объектам.
 */
export const errorString = {
  required: 'Поле обязательно для заполнения',
  tooShort: (min: number) => `Минимум ${min} символа`,
  tooLong: (max: number) => `Максимум ${max} символов`,
  /** Невалидный URL или протокол не http/https */
  invalidUrl: 'Введите корректную ссылку (http или https)',
} as const;

/** Валидация ФИО (фамилия, имя, отчество): кириллица, длина. */
export const errorFio = {
  cyrillicOnly: 'Используйте только русские буквы',
  tooLong: 'Слишком длинное значение',
} as const;

/** Денежные суммы: формат (без ограничения знаков после запятой), диапазон. */
export const errorMoneyValidation = {
  digitsOnly: 'Только цифры',
  invalidFormat: 'Введите корректную сумму',
  minPositive: 'Минимум 0.01 ₽',
  tooLarge: 'Сумма слишком большая',
} as const;

/** Единый объект ошибок для полей форм (облигации, конвертация, платежи и т.д.) */
export const errorForm = {
  // Облигации (кредит, займ, алименты)
  typeId: 'Выберите тип обязательства',
  getAccountId: 'Укажите счёт зачисления средств',
  incomeAccountId: 'Укажите счёт списания средств',
  buySum: 'Укажите сумму кредита',
  income: 'Укажите сумму платежа',
  paymentTypeId: 'Укажите тип выплат',
  incomePeriodTypeId: 'Укажите периодичность',
  buyAt: 'Укажите дату начала',
  sellAt: 'Укажите дату завершения',
  incomeExceedsBuySum: 'Сумма платежа не может превышать сумму кредита',
  sellAtBeforeBuyAt: 'Дата завершения не может быть раньше даты начала',
  commentMax: 'Максимум 500 символов',
  // Сделка (форма актива): накопленный купон по облигации — опционально, при вводе формат суммы
  tradeAccumulatedCoupon: 'Введите корректную сумму накопленного купона',
  // Конвертация актива
  tradeId: 'Укажите актив который хотите обменять',
  itemToId: 'Укажите актив на который хотите заменить',
  accountId: 'Укажите счет внесения',
  tradeAt: 'Укажите дату замены',
  couponDealTradeAt: 'Укажите дату и время выплаты',
  couponDealAccrual: 'Укажите начисление',
  tradeAtNotInFuture: 'Дата замены не может быть в будущем',
  // Перевод бумаг
  transferTradesToAccountId: 'Укажите счёт назначения',
  transferTradesCount: 'Укажите количество',
  // Платежи по целям
  sum: 'Укажите сумму платежа',
  paidAt: 'Укажите дату платежа',
  // Дивиденды / купоны (форма «Добавить оценку»)
  activeItemId: 'Выберите актив',
  currencyId: 'Укажите валюту',
  decidedAt: 'Укажите дату и время решения',
  // Оценка актива (форма «Новая оценка»)
  valueAt: 'Укажите дату и время оценки',
  // Профиль (страница «Мои данные»)
  firstName: 'Укажите имя',
  lastName: 'Укажите фамилию',
  phone: 'Введите номер телефона',
  birthAt: 'Укажите дату рождения',
  birthAtNotInFuture: 'Дата не может быть в будущем',
  birthAtMinAge18: 'Вам должно быть 18 лет',
  birthAtMaxAge100: 'Возраст не может быть больше 100 лет',
  startEnterAt: 'Укажите дату начала работы',
  startEnterAtBeforeBirthAt: 'Дата начала работы не может быть раньше даты рождения',
  vkUrl: 'Укажите ссылку на профиль VK (формат https://…)',
  // Счета
  accountTypeId: 'Выберите тип счёта',
  accountName: 'Укажите название счёта',
  accountBankId: 'Укажите банк или брокера',
  accountSubaccountName: 'Укажите название субсчёта',
  accountSubaccountSum: 'Укажите сумму',
  accountSubaccountCurrencyId: 'Укажите валюту',
  accountCreditLimitNonNegative: 'Кредитный лимит не может быть отрицательным',
  accountGracePeriodDaysNonNegative: 'Льготный период не может быть отрицательным',
  // Инвестиции (акции, облигации, ПИФ, ETF и т.д.)
  activeName: 'Введите название',
  activeTypeId: 'Выберите тип инвестиции',
  activeGroupId: 'Укажите группу инвестиций',
  activeNominalSum: 'Укажите номинальную стоимость',
  activeCurrency: 'Укажите валюту',
  activeRatePeriod: 'Укажите период выплаты купона',
  activeRateType: 'Выберите тип процента',
  // Недвижимость
  buyAccountId: 'Укажите счёт покупки',
  buySumPrice: 'Укажите цену покупки',
  buyAtPrice: 'Укажите дату и время покупки',
  sellAccountId: 'Укажите счёт продажи',
  sellSum: 'Укажите цену продажи',
  sellAtPrice: 'Укажите дату и время продажи',
  buyAtNotInFuture: 'Дата покупки не может быть в будущем',
  sellAtNotInFuture: 'Дата продажи не может быть в будущем',
  // REPO
  repoCatalogHubId: 'Укажите бумагу',
  repoActiveAccountId: 'Укажите счёт, на котором хранится бумага',
  repoToAccountId: 'Укажите счёт зачисления средств',
  repoToCurrencyId: 'Укажите валюту зачисления средств',
  repoPercent: 'Укажите процентную ставку по РЕПО',
  repoDiscount: 'Укажите дисконт по РЕПО',
  repoOperationSum: 'Укажите сумму сделки РЕПО',
  repoOpenedQuantity: 'Укажите количество бумаг для блокировки',
  repoOpenedAt: 'Укажите дату открытия сделки РЕПО',
  repoOpenedCalculateAt: 'Укажите дату начала расчетов по РЕПО',
  openedAtNotInFuture: 'Дата открытия РЕПО не может быть в будущем',
  openedCalculateAtNotInFuture: 'Дата начала расчётов по РЕПО не может быть в будущем',
  openedCalculateAtNotBeforeOpenedAt:
    'Дата начала расчётов должна быть не раньше даты открытия РЕПО',
  percentMax100: 'Процент не может быть больше 100%',
  percentMin0: 'Процент не может быть меньше 0%',
  rateMustBeNumber: 'Введите от 0 до 100%',
  discountMax100: 'Дисконт не может быть больше 100%',
  lotsizePositive: 'Количество бумаг в 1 лоте должно быть положительным числом',
  // REPO: поля обязательны при закрытии (close_repo === true)
  repoFromAccountId: 'Укажите счёт списания',
  repoFromCurrencyId: 'Укажите валюту списания',
  repoClosedQuantity: 'Укажите количество для разблокировки',
  repoCommissionSum: 'Укажите сумму сделки при закрытии',
  repoOperNumber: 'Укажите номер операции',
  repoClosedAt: 'Укажите дату закрытия сделки РЕПО',
  repoClosedCalculateAt: 'Укажите дату закрытия расчетов по РЕПО',
  closedAtNotInFuture: 'Дата закрытия РЕПО не может быть в будущем',
  closedCalculateAtNotBeforeClosedAt:
    'Дата окончания расчётов должна быть не раньше даты закрытия РЕПО',
  closedQuantityNotExceedOpened:
    'Некорректное количество закрытия РЕПО: количество для разблокировки не может превышать количество при открытии.',
} as const;

/** @deprecated Используйте errorForm */
export const errorActiveGoalPayment = {
  sum: errorForm.sum,
  paidAt: errorForm.paidAt,
} as const;

export const errorClient = {
  firstName: 'Укажите имя',
  lastName: 'Укажите фамилию',
} as const;

export const errorAuth = {
  code: 'Введите код из письма',
  passwordConfirmation: 'Подтвердите пароль',
} as const;

export const errorEmail = {
  required: 'Введите email',
  invalidate: 'Некорректный email',
  tooLong: 'Email слишком длинный',
} as const;

export const errorPassword = {
  required: 'Введите пароль',
  size: 'Минимум 6 символов',
  tooLong: 'Пароль слишком длинный',
  match: 'Пароли не совпадают',
} as const;

export const errorIntegration = {
  token: 'Введите токен',
  publicKey: 'Введите публичный ключ',
  privateKey: 'Введите приватный ключ',
} as const;

export const errorWithdraw = {
  amountRequired: 'Введите сумму для вывода',
  amountMustBePositive: 'Сумма должна быть больше нуля',
  amountExceedsAvailable: 'Сумма не должна превышать доступную для вывода',
} as const;

/** Сообщения валидации серии и номера паспорта (РФ). */
export const errorPassportValidation = {
  seriesEmpty: 'Введите серию паспорта',
  numberEmpty: 'Введите номер паспорта',
  digitsOnly: 'Только цифры',
  seriesLength: 'Серия: 4 цифры',
  numberLength: 'Номер: 6 цифр',
} as const;

/** Адрес прописки (форма договора робо): длина после trim. */
export const errorPassportAddressValidation = {
  empty: 'Введите адрес прописки',
  tooShort: 'Адрес слишком короткий',
  tooLong: 'Адрес слишком длинный',
  /** Без латиницы; допустимы кириллица, цифры и обычные знаки адреса. */
  cyrillicOnly: 'Используйте только русские буквы (латиница не допускается)',
} as const;

/** Поле «Кем выдан» (паспорт, робо): пустое значение. Min/max — в `schemaDefaults`, тексты длины — `errorString`. */
export const errorPassportIssuerValidation = {
  empty: 'Введите кем выдан',
  /** Без латиницы; допустимы кириллица, цифры, пробел, дефис, точка. */
  cyrillicOnly: 'Используйте только русские буквы (латиница не допускается)',
} as const;

export const errorSubscription = {
  /** Обязательное поле телефона (робо / единый ruPhoneSchema). */
  phone: 'Введите номер телефона',
  phoneIncomplete: 'Номер неполный',
  phoneInvalid: 'Введите корректный номер',
  lastName: 'Введите фамилию',
  name: 'Введите имя',
  passportSeries: errorPassportValidation.seriesEmpty,
  passportNumber: errorPassportValidation.numberEmpty,
  passportIssuer: errorPassportIssuerValidation.empty,
  passportAddress: errorPassportAddressValidation.empty,
  inn: 'Введите ИНН',
  snils: 'Введите СНИЛС',
  agreement: 'Необходимо согласие',
  creditLimit: 'Введите кредитный лимит',
} as const;

/** Сообщения валидации ИНН физлица (12 цифр, контрольные разряды). */
export const errorInnValidation = {
  empty: 'Введите ИНН',
  digitsOnly: 'Только цифры',
  length12: 'ИНН должен содержать 12 цифр',
  invalidChecksum: 'ИНН введён некорректно',
} as const;

/** Сообщения валидации СНИЛС (алгоритм ПФР). */
export const errorSnilsValidation = {
  empty: 'Введите СНИЛС',
  notOnlyDigits: 'СНИЛС должен состоять только из цифр',
  wrongLength: 'СНИЛС должен состоять из 11 цифр',
  threeSameDigits: 'В СНИЛС не допускаются три одинаковые цифры подряд',
  invalidChecksum: 'Неверное контрольное число СНИЛС',
} as const;
